import React, { useEffect, useMemo, useState } from 'react';
import DashedLineChart from 'Pages/GMS/Performance/Components/DashedLineChart';
import { ChartTypes } from 'Pages/GMS/Performance/Components/ChartTypes';
import ColumnChart from 'Pages/GMS/Performance/Components/ColumnChart';
import ColumnAndLineChart from 'Pages/GMS/Performance/Components/ColumnAndLineChart';
import { getABAnalytics } from '../../services/ABServices';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import NoDataComponent from 'Components/Common/NoDataComponent';
import Legends from './Legends';
import { AB_CHART_COLORS, AB_CHART_STROKE_COLORS } from '../../utils/Constants';

// Shape of each graph panel returned by the API
type GraphPanel = {
    xAxis: string[];
    data: Array<{ name: string; data: Array<{ x: string; y: number }> }>;
};

// Shape of each metric entry: normalized + actual pair
type MetricGraphData = {
    normalized: GraphPanel;
    actual: GraphPanel;
};

// Full API response: metric name -> pair of graphs
type AnalyticsData = Record<string, MetricGraphData>;

// Labels shown above each graph panel
const GRAPH_LABELS: Record<'normalized' | 'actual', string> = {
    normalized: 'Normalized',
    actual: 'Actual',
};

const ResultAnalytics = ({ testId }: { testId: string }) => {
    // selectedChartItem is per metric row: metricName -> chart type string
    const [selectedChartItems, setSelectedChartItems] = useState<Record<string, string>>({});
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
    const [metricKeys, setMetricKeys] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const columLineData = (data: GraphPanel['data']) => {
        return data.map((x) => ({
            name: x.name,
            columnData: x.data,
            lineData: x.data,
        }));
    };

    const getAnalyticsData = async () => {
        try {
            setLoading(true);
            const res = await getABAnalytics(testId);
            if (res) {
                setAnalyticsData(res as AnalyticsData);
                const keys = Object.keys(res);
                setMetricKeys(keys);
                // Initialise each metric row to "Line Chart"
                const initialSelections: Record<string, string> = {};
                keys.forEach((k) => { initialSelections[k] = 'Line Chart'; });
                setSelectedChartItems(initialSelections);
            } else {
                toast.info("Couldn't get the analytics data");
            }
        } catch (error) {
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAnalyticsData();
    }, []);

    // Build legend items from the first available metric panel's series names
    const legendItems = useMemo(() => {
        if (!analyticsData || metricKeys.length === 0) return [];
        const firstKey = metricKeys[0];
        const panel = analyticsData[firstKey]?.normalized ?? analyticsData[firstKey]?.actual;
        if (!panel) return [];
        return panel.data.map((series, idx) => ({
            name: series.name,
            strokeColor: AB_CHART_STROKE_COLORS[idx % AB_CHART_STROKE_COLORS.length],
            fillColor: AB_CHART_COLORS[idx % AB_CHART_COLORS.length],
        }));
    }, [analyticsData, metricKeys]);

    const renderGraph = (
        panel: GraphPanel | undefined,
        metricName: string,
        chartType: string
    ) => {
        if (!panel || panel.data.length === 0) {
            return (
                <div className="tw-items-center tw-font-bold tw-h-[350px]">
                    <NoDataComponent />
                </div>
            );
        }
        if (chartType === 'Line Chart') {
            return (
                <DashedLineChart
                    data={panel.data}
                    xAxis={panel.xAxis}
                    granularity="PT2D"
                    dashedArray={[]}
                    timezone="UTC"
                    metricName={metricName}
                    colors={AB_CHART_COLORS}
                />
            );
        }
        if (chartType === 'Column Chart') {
            return (
                <ColumnChart
                    data={panel.data}
                    metricName={metricName}
                    xAxis={panel.xAxis}
                    granularity="PT2D"
                    timezone="UTC"
                    colors={AB_CHART_COLORS}
                />
            );
        }
        if (chartType === 'Line & Column') {
            return (
                <ColumnAndLineChart
                    data={columLineData(panel.data)}
                    xAxis={panel.xAxis}
                    granularity="PT2D"
                    timezone="UTC"
                    colors={AB_CHART_COLORS}
                />
            );
        }
        return null;
    };

    if (loading) return (
        <div className="tw-flex tw-justify-center tw-py-10">
            <Spinner size="lg" label="Loading analytics" showLabel data-testid="ab-result-analytics-loading" />
        </div>
    );

    return (
        <section>
            <Legends items={legendItems} />
            {metricKeys.map((metricKey) => {
                const metricData = analyticsData?.[metricKey];
                const chartType = selectedChartItems[metricKey] ?? 'Line Chart';

                return (
                    // One full-width row per metric (or sub-metric when Score is selected)
                    <div key={metricKey} className="tw-mb-4">
                        {/* Row header: metric name + chart type selector */}
                        <div className="tw-flex tw-justify-between tw-items-center tw-px-2 tw-pb-2">
                            <label className="tw-text-sm tw-font-semibold tw-text-ink">
                                {metricKey}
                            </label>
                            <ChartTypes
                                selectedItem={chartType}
                                setSelectedItem={(val: string) =>
                                    setSelectedChartItems((prev) => ({ ...prev, [metricKey]: val }))
                                }
                            />
                        </div>

                        {/* Two graph panels side by side: Normalized (left) | Actual (right) */}
                        <div className="tw-flex tw-flex-wrap tw-gap-0">
                            {(['actual', 'normalized'] as const).map((side) => (
                                <div
                                    key={side}
                                    className="tw-rounded tw-w-[calc(50%-8px)] tw-m-1 tw-bg-greys-000 tw-shadow-[0px_4px_12px_rgba(0,_0,_0,_0.08)] tw-box-border tw-h-auto tw-border-[1px] tw-border-solid tw-border-edge"
                                >
                                    {/* Panel title */}
                                    <div className="tw-flex tw-items-center tw-pt-2 tw-px-3 tw-pb-2 tw-border-b-2">
                                        <label className="tw-text-sm tw-ps-1 tw-font-medium tw-text-ink">
                                            {GRAPH_LABELS[side]} {metricKey}
                                        </label>
                                    </div>

                                    {/* Chart */}
                                    {renderGraph(metricData?.[side], metricKey, chartType)}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </section>
    );
};

export default ResultAnalytics;